import { useState } from "react";
import Icon from "@mdi/react";
import { mdiAccountGroup, mdiPencil, mdiAccountPlus, mdiBriefcase, mdiBriefcaseOff, mdiRun } from "@mdi/js";
import * as v from "valibot";
import { useLang } from "../../../services/lang";
import { useUser, User, UserInput } from "../../../components/User";
import { api, handleError } from "../../../services/api";
import { ContentSwitch, ConfirmButton, ShareButton, Loading } from "../../../components";
import Dialog from "../../../components/Dialog";
import { Input } from "../../../components/Form";
import { UserSchema } from "../../../services/checkData";
import { Team } from "../../../../types";
import { getUserPermissions, getDay } from "../utils";
import { ShowEvents } from "./ShowEvents";

type Event = {
    date: string,
    eventDate?: string,
    description: string,
}

type TeamDetailProps = Team & { userHashId: string, onChange: () => void, onTeamEdit: () => void };

export function TeamDetail({ _id, title, description, users, events, userHashId, onChange, onTeamEdit }: TeamDetailProps) {
    const { dict } = useLang();
    const { userId, user, isLoggedIn, showUserDialog } = useUser();
    const [contentIndex, setContentIndex] = useState(1);

    const [message, setMessage] = useState("");

    const [showUsersDialog, setShowUsersDialog] = useState(false);

    const [editEvent, setEditEvent] = useState<null | Event>(null);
    const [editEventMessage, setEditEventMessage] = useState("");
    const [editEventLoading, setEditEventLoading] = useState(false);

    const validUser = v.safeParse(UserSchema, user);

    const eventsWithDate = Object.entries(events).map(([date, event]) => ({ date, ...event }));

    const today = getDay(0);

    const { isTeamMember, isTeamAdmin } = getUserPermissions(userHashId, users);

    const usersList = Object
        .entries(users)
        .map(([hashId, user]) => ({ hashId, ...user }))
        .sort((a, b) => a.name.localeCompare(b.name));

    async function handleAddMember() {
        const [, error] = await api.addTeamMember({ teamId: _id, user, userId });

        if (error) {
            handleError(error);
        } else {
            onChange();
        }
    }

    async function handleRemoveMember(userId: string) {
        setMessage("");
        const [res, error] = await api.removeTeamMember({ teamId: _id, userId });

        if (error) {
            handleError(error);
        } else if (res) {
            switch (res.message) {
                case "success":
                    onChange();
                    break;
                case "not-permission":
                    setMessage(dict.teamAlertPermissionRemove);
                    break;
                case "last-admin":
                    setMessage(dict.teamAlertLastAdmin);
                    break;
            }
        }
    }

    async function handleMemberAdmin(userId: string, isAdmin: boolean) {
        if (!isLoggedIn) return;

        const [res, error] = await api.updateTeamMemberAdmin({ teamId: _id, userId, isAdmin });

        if (error) {
            handleError(error);
        } else if (res) {
            switch (res.message) {
                case "success":
                    onChange();
                    break;
                case "not-permission":
                    setMessage(dict.teamAlertPermissionAdmin);
                    break;
                case "last-admin":
                    setMessage(dict.teamAlertLastAdmin);
                    break;
            }
        }
    }

    function handleEditEvent(date?: string) {
        setEditEvent({
            date: date || getDay(7, "17:00"),
            eventDate: date,
            description: events[date || ""]?.description || "",
        });
    }

    async function handleSaveEvent() {
        if (!editEvent) return;

        setEditEventLoading(true);
        const [_, error] = await api.updateTeamEvent({
            teamId: _id,
            date: editEvent.date,
            eventDate: editEvent.eventDate,
            description: editEvent.description,
        });

        if (error) {
            handleError(error);
            setEditEventMessage(dict.teamErrorMessage);
        } else {
            setEditEvent(null);
            onChange();
        }

        setEditEventLoading(false);
    }

    return <>
        {isTeamMember && <div className="buttons" style={{ marginTop: "20px" }}>
            <a className="block" onClick={() => setShowUsersDialog(true)}>
                <Icon path={mdiAccountGroup} size={1} />{isTeamAdmin ? dict.teamMemberEdit : dict.teamMemberShow}
            </a>
            {isTeamAdmin && <a className="block" onClick={() => onTeamEdit()}>
                <Icon path={mdiPencil} size={1} />{dict.teamEdit}
            </a>}
        </div>}

        <p className="page-description pre-line">{description}</p>

        <UserInput />

        {!validUser.success && <p className="info">{dict.teamUserNameEmptyText}</p>}
        {validUser.success && <>
            {!isLoggedIn && <p className="info">{dict.teamUserLoginText[0]}<a className="block" style={{ padding: "0px 8px" }} onClick={showUserDialog}>{dict.teamUserLoginText[1]}</a>.</p>}
            {!isTeamMember && <>
                <a className="block" onClick={handleAddMember}><Icon path={mdiAccountPlus} size={1} />{dict.teamMemberAddButton}</a>
            </>}
        </>}

        <ContentSwitch
            index={contentIndex}
            onChange={setContentIndex}
            options={[{
                title: dict.teamEventPast,
                content: <ShowEvents
                    teamId={_id}
                    isActive={false}
                    userHashId={userHashId}
                    users={users}
                    events={eventsWithDate
                        .filter(event => event.date < today)
                        .sort((a, b) => b.date.localeCompare(a.date))
                    }
                    onEditEvent={handleEditEvent}
                    onChange={onChange}
                />,
            }, {
                title: dict.teamEventFollowing,
                content: <ShowEvents
                    teamId={_id}
                    isActive={validUser.success}
                    userHashId={userHashId}
                    users={users}
                    events={eventsWithDate
                        .filter(event => event.date >= today)
                        .sort((a, b) => a.date.localeCompare(b.date))
                    }
                    onEditEvent={handleEditEvent}
                    onChange={onChange}
                />,
            }]}
        />

        {/* Users dialog */}
        <Dialog show={showUsersDialog} onClose={() => setShowUsersDialog(false)}>
            <div className="team-users">
                <h2>{dict.teamMemberHeader}</h2>
                <p className="info">{dict.getNumberTeamMembers(usersList.filter(user => user.isMember).length)}</p>

                <ShareButton text="Pozvat další členy" title={dict.seo.team.getTitle(title)} />

                {message && <p className="alert">{message}</p>}
                <ul>
                    {usersList.filter(user => user.isMember).map(({ hashId, ...user }) => <li key={hashId}>
                        <User {...user} showFull />
                        {isTeamAdmin && <div className="buttons">
                            <button onClick={() => handleMemberAdmin(hashId, !user.isAdmin)}>
                                <Icon path={user.isAdmin ? mdiBriefcaseOff : mdiBriefcase} size={1} />
                            </button>
                            <ConfirmButton text={dict.teamMemberRemoveText} className="block" onClick={() => handleRemoveMember(hashId)}>
                                <Icon path={mdiRun} size={1} />
                            </ConfirmButton>
                        </div>}
                    </li>)}
                </ul>
                <hr />
                <ul style={{ opacity: 0.5 }}>
                    {usersList.filter(user => !user.isMember).map(({ hashId, ...user }) => <li key={hashId}>
                        <User {...user} showFull />
                    </li>)}
                </ul>
            </div>

            <ConfirmButton text={dict.teamMemberLeaveText} className="block" onClick={() => handleRemoveMember(userId)}>
                <Icon path={mdiRun} size={1} />{dict.teamMemberLeaveButton}
            </ConfirmButton>
        </Dialog>

        {/* Edit event */}
        {isTeamAdmin && <Dialog width={500} show={!!editEvent} onClose={() => setEditEvent(null)}>
            {editEvent && <>
                <h2>{editEvent.eventDate ? dict.teamEventEdit : dict.teamEventAdd}</h2>

                <Input
                    autoFocus
                    type="datetime-local"
                    label={dict.teamEventFormDate}
                    value={editEvent}
                    field="date"
                    setValue={setEditEvent}
                    required
                />
                <Input
                    type="textarea"
                    label={dict.teamEventFormDescription}
                    value={editEvent}
                    field="description"
                    setValue={setEditEvent}
                    maxLength={500}
                />

                {editEventMessage && <div className="alert">{editEventMessage}</div>}

                {editEventLoading && <Loading />}
                <button disabled={editEventLoading} onClick={handleSaveEvent}>
                    {editEvent.eventDate ? dict.save : dict.teamEventAdd}
                </button>
            </>}
        </Dialog>}
    </>;
}
